import { Box, Button, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { OrganizationConfigurationContext } from "hooks/useOrganizationConfiguration";
import useEarnings from "hooks/earnings/useEarnings";
import theme from "lib/theme";
import { useContext, useMemo, useState } from "react";
import { ServiceTermsModal } from "./ServiceTermsModal";
import { AssetRetrievalBulkOrderDetails } from "types/assetRetrieval";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OrderSummaryLoadingComponent from "./OrderSummaryLoadingComponent";
import useAssetRetrievalPricingPerBox from "hooks/assetRetrievals/useAssetRetrievalPricingPerBox";
import { TotalRowsPerBox } from "hooks/assetRetrievals/useAssetRetrievalBulkValidation";
import useFeatureFlag from "hooks/useFeatureFlag";

interface AssetCartProps {
  onContinue: () => void;
  continueLabel?: string;
  disableContinue?: boolean;
  isLoadingFile?: boolean;
  totalRowsPerBox: TotalRowsPerBox;
}

function BulkAssetCart({
  onContinue,
  continueLabel = "Continue",
  disableContinue,
  isLoadingFile,
  totalRowsPerBox,
}: AssetCartProps) {
  const { values } = useFormikContext<AssetRetrievalBulkOrderDetails>();
  const { earningsBalance, isLoading: isEarningsLoading } = useEarnings();
  const pricingPerBox = useAssetRetrievalPricingPerBox(totalRowsPerBox, values?.virtualPickup ?? false);
  const total = Object.values(pricingPerBox).reduce((acc, pricingEntry) => acc + pricingEntry.total, 0);

  const insufficientFunds = total > earningsBalance;
  const { assetRetrievalTermsSigned } = useContext(OrganizationConfigurationContext);
  const [openTerms, setOpenTerms] = useState(false);
  const { enabled: assetRetrievalDevicesEnabled } = useFeatureFlag("asset_retrieval_devices");

  const orderSummaryRowsComponent = useMemo(() => {
    return Object.entries(pricingPerBox).map(([boxType, pricingEntry]) => {
      const quantity = totalRowsPerBox?.[boxType]?.quantity || 0;

      if (quantity === 0) return null;

      return (
        <>
          <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
              <Box>
                {boxType.charAt(0).toUpperCase() + boxType.slice(1).replace("_", " ")} Box & Shipping Flat Fee ($
                {pricingEntry.basePricePerRow} x {quantity} Boxes)
              </Box>
              <Box>${pricingEntry.basePrice}</Box>
            </Box>
            {pricingEntry.insuranceAmount > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
                <Box>Insurance Total</Box>
                <Box>${pricingEntry.insuranceAmount}</Box>
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
              <Box>
                Processing Fees (${pricingEntry.processingFeePerRow} x {quantity} Boxes){" "}
              </Box>
              <Box>${pricingEntry.processingFee}</Box>
            </Box>
            {pricingEntry.serviceFeeInCents ? (
              <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
                <Box>Data Wipe & Audit Fee</Box>
                <Box>${pricingEntry.serviceFeeInCents}</Box>
              </Box>
            ) : null}

            {pricingEntry.taxPercentage > 0 ? (
              <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
                <Box>
                  Estimated tax ({pricingEntry.taxPercentage}%)
                  <Tooltip title="This tax is based on state sales tax rates for your organization's location">
                    <IconButton size="small" sx={{ pl: 1, color: "text.secondary" }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box>${pricingEntry.taxAmount}</Box>
              </Box>
            ) : null}
            {pricingEntry.discountPercentage > 0 ? (
              <Box sx={{ display: "flex", justifyContent: "space-between", color: theme.palette.primaryPro.main }}>
                <Box>Discount ({pricingEntry.discountPercentage}%)</Box>
                <Box>-${pricingEntry.discountAmount}</Box>
              </Box>
            ) : (
              ""
            )}
            {values.virtualPickup && (
              <Box sx={{ display: "flex", justifyContent: "space-between", color: theme.palette.primaryPro.main }}>
                <Box>Waived Fees (per box)</Box>
                <Box>-${pricingEntry.total}</Box>
              </Box>
            )}
            {assetRetrievalDevicesEnabled && (
              <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
                <Box>Subtotal</Box>
                <Box>${pricingEntry.total}</Box>
              </Box>
            )}
          </Box>
          <Divider />
        </>
      );
    });
  }, [pricingPerBox, totalRowsPerBox]);

  const shouldOpenTerms = useMemo(() => {
    return continueLabel === "place order" && !assetRetrievalTermsSigned;
  }, [continueLabel, assetRetrievalTermsSigned]);

  if (
    Object.values(pricingPerBox).some((pricingEntry) => pricingEntry.isPricingLoading) ||
    isEarningsLoading ||
    isLoadingFile
  ) {
    return <OrderSummaryLoadingComponent continueLabel={continueLabel} />;
  }

  return (
    <div style={{ position: "sticky", top: "95px" }}>
      <Box
        sx={{
          py: 3,
          px: 4,
          background: "white",
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          [theme.breakpoints.down("lg")]: { px: 2, py: 2 },
        }}
      >
        <Typography fontWeight="bold" variant="h6">
          Order Summary
        </Typography>

        {orderSummaryRowsComponent}

        <Box mt={2} sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography fontSize={"20px"} fontWeight="bold" color="primary">
            Total
          </Typography>
          <Typography fontSize={"20px"} fontWeight="bold" color="primary">
            ${values.virtualPickup ? 0 : total.toFixed(2)}
          </Typography>
        </Box>
        <Box mt={1} sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}>
            New Earnings Balance: $
            {insufficientFunds ? 0 : (earningsBalance - (values.virtualPickup ? 0 : total)).toFixed(2)}
          </Box>
        </Box>
        <Box mt={5}>
          <span>
            <Button
              fullWidth
              variant="contained"
              onClick={shouldOpenTerms ? () => setOpenTerms(true) : onContinue}
              disabled={disableContinue}
            >
              {continueLabel}
            </Button>
          </span>
        </Box>
        <ServiceTermsModal open={openTerms} setOpen={setOpenTerms} submit={onContinue} />
      </Box>
    </div>
  );
}

export default BulkAssetCart;
